/**
 * Horloge du jeu. Une journee complete dure 24 minutes reelles.
 * Module pur: aucune dependance au DOM, a three.js ou a node.
 */
import {
  DAY_LENGTH_MS,
  PHASE_AUBE_DEBUT,
  PHASE_JOUR_DEBUT,
  PHASE_NUIT_DEBUT,
  PHASE_SOIR_DEBUT,
  START_HOUR,
} from '../constants.ts'

export type PhaseJour = 'aube' | 'jour' | 'soir' | 'nuit'

export interface Horloge {
  /** Temps de jeu ecoule depuis le debut de la partie, en millisecondes. */
  tempsJeu: number
  /** Numero du jour, a partir de 1. */
  jour: number
  /** Heure de jeu, 0 a 23. */
  heure: number
  /** Minute de jeu, 0 a 59. */
  minute: number
  /** Heure decimale, 0 a 24. Pratique pour les interpolations. */
  heureDecimale: number
  phase: PhaseJour
  /** Vrai pendant la nuit: les feux d'artifice ne sont permis que la. */
  nuit: boolean
  /**
   * Progression a l'interieur de la phase courante, de 0 a 1.
   * Sert aux fondus de lumiere et de couleur du ciel.
   */
  progressionPhase: number
}

/** Temps de jeu correspondant a l'heure de depart d'une nouvelle partie. */
export const TEMPS_DEPART = (START_HOUR / 24) * DAY_LENGTH_MS

const NOMS_PHASE: Record<PhaseJour, string> = {
  aube: 'Aube',
  jour: 'Jour',
  soir: 'Soir',
  nuit: 'Nuit',
}

/** Nom lisible de la phase, pour l'interface. */
export function nomPhase(p: PhaseJour): string {
  return NOMS_PHASE[p]
}

/** Pictogramme de la phase, pour l'interface. */
export function iconePhase(p: PhaseJour): string {
  switch (p) {
    case 'aube':
      return '🌅'
    case 'jour':
      return '🌞'
    case 'soir':
      return '🌇'
    case 'nuit':
      return '🌙'
  }
}

/**
 * Phase de la journee pour une heure decimale donnee, avec la progression
 * dans cette phase. La nuit traverse minuit, d'ou le cas particulier.
 */
export function phasePour(heureDecimale: number): { phase: PhaseJour; progression: number } {
  const h = heureDecimale
  if (h >= PHASE_AUBE_DEBUT && h < PHASE_JOUR_DEBUT) {
    return { phase: 'aube', progression: (h - PHASE_AUBE_DEBUT) / (PHASE_JOUR_DEBUT - PHASE_AUBE_DEBUT) }
  }
  if (h >= PHASE_JOUR_DEBUT && h < PHASE_SOIR_DEBUT) {
    return { phase: 'jour', progression: (h - PHASE_JOUR_DEBUT) / (PHASE_SOIR_DEBUT - PHASE_JOUR_DEBUT) }
  }
  if (h >= PHASE_SOIR_DEBUT && h < PHASE_NUIT_DEBUT) {
    return { phase: 'soir', progression: (h - PHASE_SOIR_DEBUT) / (PHASE_NUIT_DEBUT - PHASE_SOIR_DEBUT) }
  }
  // Nuit: de PHASE_NUIT_DEBUT a 24, puis de 0 a PHASE_AUBE_DEBUT.
  const duree = 24 - PHASE_NUIT_DEBUT + PHASE_AUBE_DEBUT
  const ecoule = h >= PHASE_NUIT_DEBUT ? h - PHASE_NUIT_DEBUT : 24 - PHASE_NUIT_DEBUT + h
  return { phase: 'nuit', progression: ecoule / duree }
}

/** Construit l'horloge a partir du temps de jeu ecoule. */
export function horlogeDepuis(tempsJeu: number): Horloge {
  const t = Math.max(0, tempsJeu)
  const jour = Math.floor(t / DAY_LENGTH_MS) + 1
  const dansLeJour = t % DAY_LENGTH_MS
  const heureDecimale = (dansLeJour / DAY_LENGTH_MS) * 24
  const heure = Math.floor(heureDecimale)
  const minute = Math.floor((heureDecimale - heure) * 60)
  const { phase, progression } = phasePour(heureDecimale)
  return {
    tempsJeu: t,
    jour,
    heure,
    minute,
    heureDecimale,
    phase,
    nuit: phase === 'nuit',
    progressionPhase: progression,
  }
}

/** Formate l'heure en HH:MM. */
export function formaterHeure(h: Horloge): string {
  return `${String(h.heure).padStart(2, '0')}:${String(h.minute).padStart(2, '0')}`
}

/** Formate la date en "Jour 12". */
export function formaterDate(h: Horloge): string {
  return `Jour ${h.jour}`
}

/**
 * Temps de jeu du prochain lever du soleil strictement apres `tempsJeu`.
 * Sert au sommeil: dormir avance l'horloge jusqu'a l'aube.
 */
export function prochaineAube(tempsJeu: number): number {
  const jour = Math.floor(tempsJeu / DAY_LENGTH_MS)
  const aubeCeJour = jour * DAY_LENGTH_MS + (PHASE_AUBE_DEBUT / 24) * DAY_LENGTH_MS
  return aubeCeJour > tempsJeu ? aubeCeJour : aubeCeJour + DAY_LENGTH_MS
}

/**
 * Hauteur du soleil dans le ciel, de -1 (minuit) a 1 (midi).
 * Le zero se situe exactement aux limites aube/nuit.
 */
export function hauteurSoleil(heureDecimale: number): number {
  // Le soleil se leve a 6h et se couche a 20h30: on centre le sinus dessus.
  const leve = PHASE_AUBE_DEBUT
  const couche = PHASE_NUIT_DEBUT
  const milieu = (leve + couche) / 2
  const demi = (couche - leve) / 2
  return Math.cos(((heureDecimale - milieu) / demi) * (Math.PI / 2))
}

/** Nombre de jours de jeu ecoules, en flottant. */
export function joursEcoules(tempsJeu: number): number {
  return tempsJeu / DAY_LENGTH_MS
}
